import { useState, useEffect, useRef } from 'react';
import { ArrowRight, CheckCircle, MapPin, Phone, Mail, Play, Star, Menu, X, Globe, Zap, Shield, TrendingUp, Plane } from 'lucide-react';
import { Link } from 'react-router-dom';
import brandLogo from '../images/logo.png';
import vedio1 from '../images/city.mp4';
import companyformtion from '../images/trade-licence.png';
import dubaiCompany from '../images/business-setup.png';
import sharjah from '../images/free-zone.png';
import DRS from '../images/dubai-company.png';
import shrj from '../images/sharjah.png';
import abud from '../images/companyformation.png';
import ajmn from '../images/business-setup.png';
import Collabrator1 from '../images/collabrator1.png';
import Collabrator2 from '../images/collabrator2.png';
import Collabrator3 from '../images/collabrator3.png';
import Collabrator4 from '../images/collabrator4.png';
import Collabrator5 from '../images/collabrator5.jpg';
import Collabrator6 from '../images/collabrator6.jpg';

export default function MakeYorBrand() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const canvasRef = useRef(null);
  const posterRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animationFrameId;
    let particles = [];
    let mouse = { x: null, y: null };

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    window.addEventListener('resize', resize);
    resize();

    const handleMouseMove = (e) => {
      mouse.x = e.clientX;
      mouse.y = e.clientY;
      // Spawn particles on move
      for (let i = 0; i < 2; i++) {
        particles.push(new Particle());
      }
    };

    window.addEventListener('mousemove', handleMouseMove);

    class Particle {
      constructor() {
        this.x = mouse.x || Math.random() * canvas.width;
        this.y = mouse.y || Math.random() * canvas.height;
        this.size = Math.random() * 3 + 1;
        this.speedX = Math.random() * 3 - 1.5;
        this.speedY = Math.random() * 3 - 1.5;
        this.color = Math.random() > 0.5 ? '#e11d48' : '#ffffff';
        this.opacity = 1;
      }
      update() {
        this.x += this.speedX;
        this.y += this.speedY;
        if (this.opacity > 0.01) this.opacity -= 0.01;
      }
      draw() {
        ctx.globalAlpha = this.opacity;
        ctx.fillStyle = this.color;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
        // Add glow
        ctx.shadowBlur = 10;
        ctx.shadowColor = this.color;
      }
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (let i = 0; i < particles.length; i++) {
        particles[i].update();
        particles[i].draw();
        if (particles[i].opacity <= 0.01) {
          particles.splice(i, 1);
          i--;
        }
      }
      animationFrameId = requestAnimationFrame(animate);
    };
    animate();

    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
      
      if (posterRef.current) {
        const rect = posterRef.current.getBoundingClientRect();
        const sectionHeight = rect.height;
        const viewHeight = window.innerHeight;
        
        const start = window.scrollY + rect.top;
        const end = start + sectionHeight - viewHeight;
        
        const progress = Math.min(1, Math.max(0, (window.scrollY - start) / (end - start)));
        
        // Use CSS variable for high-performance updates without React re-renders
        posterRef.current.style.setProperty('--scroll-progress', progress);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial sync

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('active');
        }
      });
    }, { threshold: 0.1 });

    document.querySelectorAll('.reveal-on-scroll').forEach(el => observer.observe(el));

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', handleMouseMove);
      cancelAnimationFrame(animationFrameId);
      observer.disconnect();
    };
  }, []);

  return (
    <div className="bg-black text-white relative" style={{fontFamily:"'Outfit', sans-serif"}}>
      {/* ── GALAXY MOUSE EFFECT ── */}
      <canvas 
        ref={canvasRef} 
        className="fixed inset-0 pointer-events-none z-[9999] opacity-60"
      />

      <nav 
        className={`fixed top-0 w-full z-[100] transition-all duration-700 ${
          scrolled 
            ? 'py-4 bg-black/90 backdrop-blur-2xl border-b border-red-600/30 shadow-[0_10px_50px_rgba(0,0,0,0.8)]' 
            : 'py-8 bg-transparent'
        }`}
      >
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 flex justify-between items-center">
          {/* ── PREMIUM LOGO CONTAINER ── */}
          <Link 
            to="/" 
            className={`absolute left-6 md:left-12 transition-all duration-700 ease-in-out ${
              scrolled ? 'top-2' : 'top-4'
            }`}
          >
            <div className="relative group">
              <div className="absolute -inset-8 bg-red-600/5 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
              <img 
                src={brandLogo} 
                alt="Make Your Brand" 
                className={`relative z-10 transition-all duration-700 object-contain brightness-125 animate-periodic-rotate ${
                  scrolled ? 'h-24 md:h-32' : 'h-40 md:h-64'
                }`} 
              />
            </div>
          </Link>

          {/* Spacer for the absolute logo */}
          <div className={`${scrolled ? 'w-32 md:w-48' : 'w-48 md:w-80'} transition-all duration-700`}></div>

          <div className="hidden lg:flex gap-12 items-center">
            <div className="flex gap-10 text-[13px] font-black tracking-[0.2em] uppercase">
              {['services','packages','about','contact'].map(s=>(
                <a 
                  key={s} 
                  href={`#${s}`} 
                  className="relative group py-2 overflow-hidden text-gray-300 hover:text-white transition-colors duration-300"
                >
                  <span className="relative z-10">{s}</span>
                  <span className="absolute bottom-0 left-0 w-full h-[1px] bg-red-600 -translate-x-[101%] group-hover:translate-x-0 transition-transform duration-500"></span>
                </a>
              ))}
            </div>
            
            <Link to="/contact-us">
              <button className="relative group px-10 py-4 bg-red-600 rounded-full font-black text-[12px] tracking-[0.2em] uppercase overflow-hidden shadow-[0_0_30px_rgba(220,38,38,0.3)]">
                <span className="absolute inset-0 bg-white translate-y-full group-hover:translate-y-0 transition-transform duration-500"></span>
                <span className="relative text-white group-hover:text-black transition-colors duration-500">Get Started</span>
              </button>
            </Link>
          </div>

          <button className="lg:hidden text-white p-3 bg-red-600/20 rounded-xl backdrop-blur-md border border-red-600/30" onClick={()=>setMobileOpen(!mobileOpen)}>
            {mobileOpen ? <X size={32} /> : <Menu size={32} />}
          </button>
        </div>

        {/* Mobile Menu */}
        <div className={`lg:hidden fixed inset-x-0 top-0 h-screen bg-black/98 backdrop-blur-3xl transition-all duration-700 ease-in-out ${mobileOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-full'}`}>
          <div className="flex justify-end p-8">
             <button onClick={()=>setMobileOpen(false)} className="text-white p-3 bg-red-600/20 rounded-xl">
               <X size={32} />
             </button>
          </div>
          <div className="px-10 py-10 flex flex-col gap-8 text-left">
            {['services','packages','about','contact'].map((s, i)=>(
              <a 
                key={s} 
                href={`#${s}`} 
                onClick={()=>setMobileOpen(false)} 
                className="text-4xl font-black tracking-tighter uppercase text-white hover:text-red-600 transition-all"
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                {s}
              </a>
            ))}
            <div className="h-[1px] w-full bg-white/10 my-4"></div>
            <Link to="/contact-us" onClick={()=>setMobileOpen(false)}>
              <button className="w-full py-6 rounded-2xl bg-red-600 text-xl font-black tracking-widest uppercase shadow-2xl">
                Get Started
              </button>
            </Link>
          </div>
        </div>
      </nav>

      {/* ── HERO ── */}
      <section className="min-h-screen relative flex items-center justify-center overflow-hidden pt-20">
        <div className="absolute inset-0 z-0">
          <video autoPlay muted loop playsInline
                 className="w-full h-full object-cover scale-110"
                 style={{filter:'brightness(0.3) saturate(1.2)'}}>
            <source src={vedio1} type="video/mp4"/>
          </video>
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-black" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(225,29,72,0.15)_0%,transparent_70%)]" />
        </div>

        <div className="relative z-10 text-center px-6 max-w-6xl mx-auto pt-32">
          <h1 className="font-bold leading-[1.1] mb-8 animate-fade-up tracking-tight"
              style={{ fontSize: 'clamp(2.5rem, 8vw, 4.5rem)', animationDelay: '0.4s' }}>
            <span className="text-white block">Build Your</span>
            <span className="text-primary-gradient block">Brand Empire</span>
            <span className="text-white/40 block">in Dubai</span>
          </h1>

          <p className="text-gray-400 text-xl md:text-2xl max-w-3xl mx-auto mb-12 leading-relaxed animate-fade-up"
             style={{ animationDelay: '0.6s' }}>
            We provide the elite infrastructure and strategic guidance for entrepreneurs to dominate the Middle Eastern market.
          </p>

          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center animate-fade-up mb-20"
               style={{ animationDelay: '0.8s' }}>
            <Link to="/services">
              <button className="relative group px-12 py-5 rounded-full font-black text-lg tracking-widest uppercase overflow-hidden shadow-[0_20px_50px_rgba(225,29,72,0.3)]">
                <span className="absolute inset-0 bg-red-600 transition-transform duration-500 group-hover:scale-105"></span>
                <span className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-500"></span>
                <span className="relative flex items-center gap-3">
                  Start Your Journey <ArrowRight size={22}/>
                </span>
              </button>
            </Link>
            <Link to="/contact-us">
              <button className="px-12 py-5 rounded-full font-black text-lg tracking-widest uppercase glass hover:bg-white/10 transition-all flex items-center gap-3">
                <Play size={20} className="fill-white" /> Free Consultation
              </button>
            </Link>
          </div>

          {/* Stats Bar */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto animate-fade-up"
               style={{ animationDelay: '1s' }}>
            {[
              {v:'500+',l:'Visions Realized', i: <Globe className="text-red-500" />},
              {v:'98%',l:'Success Index', i: <TrendingUp className="text-red-500" />},
              {v:'15+',l:'Year Legacy', i: <Shield className="text-red-500" />}
            ].map((s,i)=>(
              <div key={i} className="glass rounded-3xl p-8 group hover:border-red-500/50 transition-all duration-500">
                <div className="flex justify-center mb-4 transform group-hover:scale-110 transition-transform">{s.i}</div>
                <div className="text-4xl font-black mb-1">{s.v}</div>
                <div className="text-gray-500 text-xs font-bold uppercase tracking-widest">{s.l}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3 animate-bounce opacity-30">
          <span className="text-[10px] tracking-[0.4em] font-bold text-white uppercase">Discover</span>
          <div className="w-[1px] h-12 bg-gradient-to-b from-red-600 to-transparent"></div>
        </div>
      </section>

      {/* ── COLLABORATORS (Strategic Partners) ── */}
      <section className="py-24 bg-black/50 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-center text-[10px] uppercase tracking-[0.6em] text-gray-500 mb-16 font-bold">
            Strategic Partnerships & Government Affiliations
          </p>
          <div className="flex flex-wrap justify-center items-center gap-16 md:gap-24 opacity-60 hover:opacity-100 transition-opacity duration-700">
            {[Collabrator1, Collabrator2, Collabrator3, Collabrator4, Collabrator5, Collabrator6].map((logo, i) => (
              <img key={i} src={logo} alt="Partner" className="h-16 md:h-20 w-auto object-contain grayscale hover:grayscale-0 transition-all duration-500" />
            ))}
          </div>
        </div>
      </section>

      {/* ── CORE COMPETENCIES (Services) ── */}
      <section className="py-40 relative overflow-hidden" id="services">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-32 reveal-on-scroll">
            <span className="inline-block px-6 py-2 rounded-full glass border-red-500/20 text-red-500 text-[10px] font-black tracking-[0.4em] mb-8 uppercase">
              Core Competencies
            </span>
            <h2 className="text-5xl md:text-8xl font-bold mb-8 tracking-tight">
              Elite Business <span className="text-primary-gradient">architecture</span>
            </h2>
          </div>

          <div className="grid gap-32">
            {[
              {img:companyformtion, title:'Trade Licence Formation', desc:'Precision engineering of your corporate legal identity in the UAE mainland. Total market accessibility with strategic oversight.',
               items:['100% Ownership Architecture','Activity Strategic Mapping','Local Regulatory Liaison','Rapid Deployment Protocol']},
              {img:dubaiCompany, title:'Freezone Strategic Setup', desc:'Optimized tax efficiency and profit repatriation through curated freezone jurisdictions tailored to your vertical.',
               items:['Tax Exemption Engineering','Infrastructure Optimization','Global Connectivity Access','Repatriation Security']},
            ].map((service, i) => (
              <div key={i} className={`flex flex-col ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} items-center gap-20 md:gap-32 mb-48 reveal-on-scroll`}>
                <div className="w-full md:w-1/2">
                  <div className="relative group">
                    <div className="absolute -inset-4 bg-red-600/20 blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
                    <img 
                      src={service.img} 
                      alt={service.title} 
                      className="relative w-full rounded-[2rem] shadow-2xl border border-white/10 group-hover:scale-[1.02] transition-transform duration-700" 
                    />
                  </div>
                </div>
                <div className="w-full md:w-1/2">
                  <div className="w-20 h-1 bg-red-600 mb-10 shadow-[0_0_20px_rgba(227,30,36,0.5)]" />
                  <h3 className="text-5xl md:text-7xl font-bold mb-10 tracking-tight leading-tight">{service.title}</h3>
                  <p className="text-gray-400 text-xl mb-12 font-light leading-relaxed">
                    {service.desc}
                  </p>
                  <div className="grid grid-cols-2 gap-8 mb-12">
                    {service.items.map((point, idx) => (
                      <div key={idx} className="flex items-center gap-4 text-gray-300 group">
                        <div className="w-10 h-10 rounded-full bg-red-600/10 border border-red-600/20 flex items-center justify-center text-red-500 group-hover:bg-red-600 group-hover:text-white transition-all duration-300">
                          <CheckCircle size={18} />
                        </div>
                        <span className="text-sm font-bold tracking-wide uppercase">{point}</span>
                      </div>
                    ))}
                  </div>
                  <Link to="/services">
                    <button className="bg-white text-black px-12 py-5 rounded-full text-sm font-black tracking-[0.2em] uppercase hover:bg-red-600 hover:text-white transition-all shadow-xl">
                      Explore Framework
                    </button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── MAINLAND FORMATIONS CARDS ── */}
      <section id="packages" className="py-32 relative bg-black">
        <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-red-600/5 blur-[120px] rounded-full" />
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center mb-24 reveal-on-scroll">
            <span className="inline-block px-5 py-2 rounded-full glass border-red-500/30 text-red-500 text-[10px] font-black tracking-[0.4em] mb-6 uppercase">
              Global Jurisdictions
            </span>
            <h2 className="text-4xl md:text-6xl font-bold">
              Strategic <span className="text-primary-gradient">locations</span>
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {img:DRS, title:'Dubai Registration', desc:'Mastering the heart of the UAE economy. Complete logistical and regulatory licensing.',
               items:['Employment Visa','Investor Visa','Family Sponsorship','Medical Assistance']},
              {img:shrj, title:'Sharjah Formation', desc:'The cultural & industrial gateway. 100% ownership with optimized overheads.',
               items:['Strategic Location','Cost-Effective Setup','Tax-Free Environment','Affordable Offices']},
              {img:abud, title:'Abu Dhabi Formation', desc:'Capital-grade infrastructure for global enterprises. Access to the sovereign wealth core.',
               items:['100% Foreign Ownership','Tax Exemptions','Fast Registration','Global Access']},
              {img:ajmn, title:'Ajman Formation', desc:'The agile entrepreneurial hub. Rapid licensing with minimalist regulatory barriers.',
               items:['Affordable Costs','Flexible Structures','Fast Licensing','Quick Visa Services']},
            ].map((card,i)=>(
              <div key={i}
                   className="group glass rounded-[2.5rem] overflow-hidden hover:border-red-500/40 transition-all duration-700 reveal-on-scroll"
                   style={{ transitionDelay: `${i * 100}ms` }}>
                <div className="h-64 overflow-hidden">
                  <img src={card.img} alt={card.title} className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-110 transition-all duration-700"/>
                </div>
                <div className="p-10">
                  <h3 className="text-2xl font-black mb-4 group-hover:text-red-500 transition-colors">{card.title}</h3>
                  <p className="text-gray-500 text-sm mb-8 leading-relaxed line-clamp-3">{card.desc}</p>
                  <ul className="space-y-3 mb-10">
                    {card.items.map((it,j)=>(
                      <li key={j} className="flex items-center gap-3 text-xs text-gray-400 font-bold uppercase tracking-widest">
                        <div className="w-1.5 h-1.5 rounded-full bg-red-600 shadow-[0_0_8px_rgba(220,38,38,0.6)]" /> {it}
                      </li>
                    ))}
                  </ul>
                  <Link to="/contact-us" className="block">
                    <button className="w-full py-4 rounded-2xl glass border-red-500/20 font-black text-[10px] tracking-[0.3em] uppercase hover:bg-red-600 hover:text-white transition-all duration-500">
                      Initiate Setup
                    </button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHY US ── */}
      <section id="about" className="py-32 relative overflow-hidden bg-[#050505]">
        <div className="absolute bottom-0 left-0 w-1/2 h-1/2 bg-red-600/5 blur-[150px] rounded-full" />
        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <div className="flex flex-col md:flex-row items-end justify-between mb-24 reveal-on-scroll">
            <div className="max-w-2xl">
               <span className="inline-block px-5 py-2 rounded-full glass border-red-500/30 text-red-500 text-[10px] font-black tracking-[0.4em] mb-6 uppercase">
                The Advantage
              </span>
              <h2 className="text-4xl md:text-6xl font-bold">
                Engineered for <span className="text-primary-gradient">victory</span>
              </h2>
            </div>
            <p className="text-gray-500 text-xl font-light md:max-w-xs mt-8 md:mt-0 italic">"The difference between setup and success is strategy."</p>
          </div>

          <div className="grid md:grid-cols-3 gap-12">
            {[
              {icon:<Zap size={40} />,title:'Rapid Deployment',desc:'Strategic registration protocols that cut through bureaucracy, launching your vision in as little as 15 minutes.'},
              {icon:<Shield size={40} />,title:'Total Compliance',desc:'Military-grade regulatory adherence. We handle the government complexities so you can focus on the empire.'},
              {icon:<Globe size={40} />,title:'Expert Sovereignty',desc:'A decade and a half of navigating the UAE market. Deep local wisdom across all seven emirates.'},
            ].map((w,i)=>(
              <div key={i} className="group glass rounded-[2rem] p-12 hover:bg-white/[0.02] transition-all duration-700 reveal-on-scroll"
                   style={{ transitionDelay: `${i * 150}ms` }}>
                <div className="w-20 h-20 rounded-3xl glass border-red-500/20 flex items-center justify-center mb-10 text-red-500 group-hover:scale-110 group-hover:bg-red-600 group-hover:text-white transition-all duration-500">
                  {w.icon}
                </div>
                <h3 className="text-2xl font-black mb-4 tracking-tight">{w.title}</h3>
                <p className="text-gray-400 leading-relaxed font-light text-lg">{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section className="py-32 relative bg-black overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-24 reveal-on-scroll">
             <h2 className="text-4xl md:text-6xl font-bold">
              Client <span className="text-primary-gradient">endorsements</span>
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {name:'Ahmed Al Rashid',role:'CEO, TechStart Dubai',text:'Make Your Brand set up my mainland company with surgical precision. Their transparency is the new industry gold standard.'},
              {name:'Sarah Mitchell',role:'Founder, Luxe Retail LLC',text:'The ultimate concierge for UAE business setup. Flawless execution from trade license to investor visa processing.'},
              {name:'Raj Patel',role:'Director, Global Trading Co.',text:'A masterclass in professional consultancy. Our freezone entity was operational in record time. Highly recommended.'},
            ].map((t,i)=>(
              <div key={i} className="glass rounded-[2rem] p-12 text-left relative group reveal-on-scroll"
                   style={{ transitionDelay: `${i * 100}ms` }}>
                <div className="flex gap-1 mb-8">
                  {[...Array(5)].map((_,j)=><Star key={j} size={14} fill="#e11d48" className="text-red-600" />)}
                </div>
                <p className="text-gray-300 text-xl mb-10 leading-relaxed italic font-light">"{t.text}"</p>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-red-600 to-red-900 border border-white/10" />
                  <div>
                    <div className="font-black text-white tracking-tight">{t.name}</div>
                    <div className="text-red-500 text-xs font-bold uppercase tracking-widest">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CINEMATIC BILLBOARD (Horizontal Scroll on Vertical Swipe) ── */}
      <section className="relative" style={{ height: '500vh' }} ref={posterRef}>
        <div className="sticky top-0 h-screen w-full flex items-center overflow-hidden bg-[#050505] border-y border-white/5">
          {/* Visual Progress Bar (Elite Design) */}
          <div className="absolute bottom-10 left-10 right-10 h-[1px] bg-white/10 z-[100] overflow-hidden">
            <div className="h-full bg-red-600 transition-none"
                 style={{ width: 'calc(var(--scroll-progress, 0) * 100%)' }} />
          </div>

          {/* Massive Sliding Content */}
          <div 
            className="flex h-full items-center will-change-transform"
            style={{ 
              transform: 'translateX(calc(var(--scroll-progress, 0) * -400vw))',
              width: '500vw' 
            }}
          >
            {/* Slide 1: Brand Power */}
            <div className="w-[100vw] h-full flex flex-col justify-center px-20 relative flex-shrink-0">
              <div className="absolute inset-0 opacity-20">
                <img src="https://images.unsplash.com/photo-1512453979798-5ea266f8880c?auto=format&fit=crop&q=80&w=2000" className="w-full h-full object-cover" alt="Dubai" />
              </div>
              <h2 className="text-[20vw] font-bold leading-none tracking-tighter text-white/5 whitespace-nowrap absolute top-1/2 -translate-y-1/2 left-0 select-none">
                DUBAI EMPIRE
              </h2>
              <div className="relative z-10">
                <span className="text-red-500 text-sm font-black tracking-[1em] uppercase mb-12 block">Architecture 01</span>
                <h3 className="text-7xl md:text-9xl font-bold leading-tight mb-12">
                  Architecting <br/> <span className="text-primary-gradient">Success.</span>
                </h3>
              </div>
            </div>

            {/* Slide 2: Global Reach */}
            <div className="w-[100vw] h-full flex items-center px-20 bg-[#050505] relative overflow-hidden flex-shrink-0">
              <div className="absolute inset-0 opacity-40">
                <img src="https://images.unsplash.com/photo-1540962351504-03099e0a754b?auto=format&fit=crop&q=80&w=2000" className="w-full h-full object-cover" alt="Private Jet" />
              </div>
              <div className="relative z-10 max-w-4xl">
                <span className="text-red-500 text-sm font-black tracking-[1em] uppercase mb-12 block">Mobility 02</span>
                <h3 className="text-7xl md:text-[10rem] font-bold leading-none mb-16 tracking-tighter">
                  GLOBAL <br/> <span className="text-white/20">MOBILITY</span>
                </h3>
              </div>
            </div>

            {/* Slide 3: Sovereignty */}
            <div className="w-[100vw] h-full flex items-center px-20 bg-black relative overflow-hidden flex-shrink-0">
               <div className="absolute inset-0 opacity-20">
                <img src="https://images.unsplash.com/photo-1512453979798-5ea266f8880c?auto=format&fit=crop&q=80&w=2000" className="w-full h-full object-cover grayscale" alt="Dubai" />
              </div>
              <div className="relative z-10">
                <span className="text-red-500 text-sm font-black tracking-[1em] uppercase mb-12 block">Strategy 03</span>
                <h3 className="text-7xl md:text-9xl font-bold leading-tight mb-12">
                  Golden <br/> <span className="text-primary-gradient">Sovereignty.</span>
                </h3>
              </div>
            </div>

            {/* Slide 4: Future Ready */}
            <div className="w-[100vw] h-full flex items-center justify-center bg-black px-20 flex-shrink-0">
              <div className="text-center relative">
                <h2 className="text-[25vw] font-bold text-white/5 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 whitespace-nowrap">
                  EST. 2010
                </h2>
                <div className="relative z-10">
                  <h3 className="text-8xl md:text-[12rem] font-bold tracking-tighter mb-12">
                    YOUR <span className="text-primary-gradient">EMPIRE</span> <br/> STARTS NOW
                  </h3>
                  <Link to="/contact-us">
                    <button className="bg-red-600 text-white px-20 py-8 rounded-full text-2xl font-bold hover:scale-105 transition-all shadow-[0_30px_60px_rgba(227,30,36,0.3)]">
                      Secure Your Future <ArrowRight className="inline ml-4" size={32} />
                    </button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}

      {/* ── FAQ ── */}

      {/* ── FAQ ── */}

      {/* ── FAQ ── */}

      {/* ── FAQ ── */}
      <section className="py-32 relative bg-[#050505]">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-24 reveal-on-scroll">
            <h2 className="text-5xl font-black tracking-tighter">Strategic <span className="text-primary-gradient">Insights</span></h2>
          </div>
          <div className="space-y-6">
            {[
              {q:'What is the capital requirement for Dubai setup?',a:'Mainland infrastructure starts at AED 19,999. Freezone pathways range from AED 5,999 to 12,999. We engineer the cost structure based on your specific operational activity.'},
              {q:'How rapid is the formation protocol?',a:'Digital pathways allow for 15-minute registration. Physical mainland protocols typically achieve full licensure within 7 to 14 business days.'},
              {q:'Is local sponsorship mandatory for my vision?',a:'Freezone entities grant 100% foreign sovereignty. Recent mainland legislative updates now allow 100% ownership for most commercial and industrial activities.'},
              {q:'What documentation is critical for the launch?',a:'Valid passport biometric data, NOC where applicable, strategic MOA, and verified corporate physical address data.'},
            ].map((f,i)=>(
              <div key={i} className="group glass rounded-3xl p-8 hover:border-red-500/40 transition-all duration-500 reveal-on-scroll">
                <h3 className="font-black text-xl text-white mb-4 flex items-center gap-4 group-hover:text-red-500 transition-colors">
                  <div className="w-2 h-2 rounded-full bg-red-600" /> {f.q}
                </h3>
                <p className="text-gray-500 leading-relaxed font-light text-lg pl-6">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── LIAISON COMMAND CENTER (Premium Contact Redesign) ── */}
      <section className="relative py-48 bg-black overflow-hidden" id="contact">
        {/* Background Accents */}
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-red-600/5 blur-[150px] rounded-full animate-pulse" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-red-600/5 blur-[150px] rounded-full" />
        
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 relative z-10">
          <div className="flex flex-col lg:flex-row gap-32 items-stretch">
            
            {/* Left: Liaison Identity */}
            <div className="lg:w-5/12 reveal-on-scroll">
              <div className="flex items-center gap-4 mb-12">
                <div className="w-12 h-[1px] bg-red-600" />
                <span className="text-red-500 text-[10px] font-black tracking-[0.5em] uppercase">Global Liaison</span>
              </div>
              
              <h2 className="text-6xl md:text-[7rem] font-bold leading-[0.9] tracking-tighter mb-16">
                ARCHITECT <br/> YOUR <span className="text-primary-gradient">EMPIRE.</span>
              </h2>

              <div className="space-y-16">
                {/* Contact Card 1: UAE */}
                <div className="group cursor-default">
                  <h4 className="text-gray-500 text-[10px] font-black tracking-[0.4em] uppercase mb-6 flex items-center gap-3">
                    <span className="w-2 h-2 rounded-full bg-red-600 shadow-[0_0_10px_rgba(227,30,36,0.8)]" />
                    UAE Headquarters
                  </h4>
                  <div className="flex gap-8 items-start">
                    <div className="text-white/20 group-hover:text-red-600 transition-colors duration-500">
                      <MapPin size={40} strokeWidth={1} />
                    </div>
                    <p className="text-2xl md:text-3xl font-light text-gray-300 leading-tight">
                      Season Travels, Floor 7 <br/>
                      <span className="text-white font-bold">Office 705, Mai Tower</span> <br/>
                      Al Nahda 1, Dubai
                    </p>
                  </div>
                </div>

                {/* Contact Card 2: Liaison Channels */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                  <div className="group">
                    <h4 className="text-gray-500 text-[10px] font-black tracking-[0.4em] uppercase mb-4">Direct Link</h4>
                    <p className="text-2xl font-bold text-white group-hover:text-red-500 transition-colors">+971 4 565 0900</p>
                  </div>
                  <div className="group">
                    <h4 className="text-gray-500 text-[10px] font-black tracking-[0.4em] uppercase mb-4">Secure Mail</h4>
                    <p className="text-2xl font-bold text-white lowercase group-hover:text-red-500 transition-colors">info@myb.ae</p>
                  </div>
                </div>

                {/* Status Indicator */}
                <div className="inline-flex items-center gap-4 px-8 py-4 rounded-full glass border border-white/5">
                  <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                  <span className="text-[10px] font-black tracking-widest uppercase text-gray-400">Command Center Active: 09:00 - 18:00 GST</span>
                </div>
              </div>
            </div>

            {/* Right: Integrated Cartography */}
            <div className="lg:w-7/12 relative reveal-on-scroll" style={{transitionDelay: '200ms'}}>
              <div className="absolute inset-0 bg-red-600/10 blur-[100px] -z-10" />
              <div className="h-full min-h-[600px] rounded-[3.5rem] overflow-hidden border border-white/10 relative group">
                <iframe 
                  src="https://maps.google.com/maps?q=25.2620039,55.3286625&z=17&output=embed" 
                  width="100%" 
                  height="100%" 
                  style={{ border: 0, filter: 'grayscale(1) brightness(0.8) contrast(1.2)' }} 
                  allowFullScreen="" 
                  loading="lazy" 
                  referrerPolicy="no-referrer-when-downgrade">
                </iframe>
                
                {/* Elite Map Overlay */}
                <div className="absolute top-8 left-8 p-6 glass rounded-3xl border-white/10 backdrop-blur-3xl max-w-xs pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-700">
                  <h5 className="text-white font-bold mb-2 uppercase text-xs tracking-widest">Global Node: Dubai</h5>
                  <p className="text-gray-400 text-[10px] leading-relaxed">Mai Tower serves as our strategic logistics core for the Middle Eastern markets.</p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── CTA BANNER ── */}

      {/* ── CTA BANNER ── */}
      <section className="py-32 relative overflow-hidden bg-black">
        <div className="absolute inset-0 bg-red-600" />
        <div className="absolute inset-0 bg-gradient-to-r from-red-700 via-red-600 to-red-800 animate-pulse opacity-50" />
        <div className="absolute inset-0 shimmer-bg opacity-20" />
        <div className="max-w-5xl mx-auto px-6 text-center relative z-10 reveal-on-scroll">
          <h2 className="text-5xl md:text-7xl font-black text-white mb-8 tracking-tighter">Command Your Future Today.</h2>
          <p className="text-red-100 text-2xl mb-14 font-light max-w-2xl mx-auto">Elite consultancy starts with a single dialogue. Your 30-minute strategic response window starts now.</p>
          <Link to="/contact-us">
            <button className="bg-white text-black font-black text-xl px-16 py-6 rounded-full hover:scale-105 transition-all shadow-[0_30px_60px_rgba(0,0,0,0.4)]">
              Secure Free Consultation <ArrowRight className="inline ml-3" size={24}/>
            </button>
          </Link>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="bg-black pt-24 pb-12 px-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-start mb-20 gap-12">
            <div className="max-w-sm">
              <img src={brandLogo} alt="Make Your Brand" className="h-32 md:h-48 w-auto object-contain brightness-125 mb-8"/>
              <p className="text-gray-500 leading-relaxed font-light">Dubai's premier business architecture firm. Engineering sustainable corporate empires for the world's most ambitious entrepreneurs.</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-16">
              <div>
                <h4 className="font-black text-xs tracking-[0.3em] uppercase text-red-500 mb-8">Ecosystem</h4>
                <ul className="space-y-4 text-gray-400 text-sm font-bold uppercase tracking-widest">
                  {['Mainland','Freezone','PRO Services','Visa Ops'].map(s=>(
                    <li key={s}><a href="#services" className="hover:text-white transition-colors">{s}</a></li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="font-black text-xs tracking-[0.3em] uppercase text-red-500 mb-8">Empire</h4>
                <ul className="space-y-4 text-gray-400 text-sm font-bold uppercase tracking-widest">
                  <li><Link to="/services" className="hover:text-white transition-colors">Frameworks</Link></li>
                  <li><Link to="/contact-us" className="hover:text-white transition-colors">Direct Liaison</Link></li>
                  <li><a href="#" className="hover:text-white transition-colors">Success Briefs</a></li>
                </ul>
              </div>
              <div className="hidden md:block">
                <h4 className="font-black text-xs tracking-[0.3em] uppercase text-red-500 mb-8">Liaison</h4>
                <div className="space-y-4 text-gray-400 text-sm font-bold tracking-widest">
                  <p className="flex gap-3 items-center"><Phone size={14} className="text-red-500"/> +971 4 565 0900</p>
                  <p className="flex gap-3 items-center lowercase"><Mail size={14} className="text-red-500"/> info@myb.ae</p>
                  <p className="flex gap-3 items-start uppercase leading-relaxed"><MapPin size={14} className="text-red-500 mt-1"/> Mai Tower, Al Nahda 1, Dubai, UAE</p>
                </div>
              </div>
            </div>
          </div>
          <div className="pt-12 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-6 text-gray-600 text-[10px] font-black tracking-[0.3em] uppercase">
            <p>© {new Date().getFullYear()} MAKE YOUR BRAND BUSINESS SERVICES L.L.C.</p>
            <div className="flex gap-8">
              <a href="#" className="hover:text-white">Privacy Policy</a>
              <a href="#" className="hover:text-white">Terms of Protocol</a>
            </div>
            <p>Architecting Success Since 2010</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
